import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface PlatformTicket {
  id: string;
  tenant_id: string;
  subject: string;
  requester_name: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  last_message_at: string | null;
  message_count: number;
  responder_unread_count: number;
  assigned_to: string | null;
  created_at: string;
}

export interface PlatformMessage {
  id: string;
  author_name: string;
  body: string;
  attachments?: PlatformAttachment[];
  delivery_status: "sending" | "delivered";
  moderation_status: "visible" | "held" | "removed";
  created_at: string;
}

export interface PlatformAttachment {
  filename: string;
  content_type: string;
  size_bytes: number;
  object_path: string;
}

function websocketUrl(ticketId: string): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  const origin = configured?.startsWith("http")
    ? new URL(configured).origin
    : window.location.origin;
  const url = new URL(`/api/helpdesk/tickets/${ticketId}/ws`, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function usePlatformTickets(params: { limit: number; offset: number }) {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  return useQuery<{ items: PlatformTicket[]; total: number }>({
    queryKey: ["platform-support-tickets", params],
    queryFn: () =>
      api.get<{ items: PlatformTicket[]; total: number }>(
        `/api/helpdesk/platform/tickets?${query.toString()}`,
      ),
    refetchInterval: 10_000,
  });
}

export function usePlatformMessages(ticketId: string | null) {
  return useQuery<{ items: PlatformMessage[]; total: number }>({
    queryKey: ["platform-support-messages", ticketId],
    queryFn: () =>
      api.get<{ items: PlatformMessage[]; total: number }>(
        `/api/helpdesk/platform/tickets/${ticketId}/messages`,
      ),
    enabled: ticketId !== null,
    refetchInterval: ticketId === null ? false : 5000,
  });
}

export function usePlatformSupportRealtime(ticketId: string | null) {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (!ticketId) return;
    const firstToken = api.getAccessToken();
    if (!firstToken) return;
    let stopped = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(websocketUrl(ticketId), [
        "bearer",
        api.getAccessToken() ?? firstToken,
      ]);
      socket.onopen = () => {
        retry = 800;
      };
      socket.onmessage = (event) => {
        try {
          const frame = JSON.parse(String(event.data)) as { type?: string };
          if (frame.type !== "helpdesk.message.created") return;
          void queryClient.invalidateQueries({
            queryKey: ["platform-support-messages", ticketId],
          });
          void queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
        } catch {
          // Polling remains available if a malformed frame is received.
        }
      };
      socket.onclose = () => {
        if (stopped) return;
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 1.6, 8000);
      };
    };
    connect();
    return () => {
      stopped = true;
      socket?.close();
    };
  }, [queryClient, ticketId]);
}

export function useSendPlatformMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ticketId: string;
      body: string;
      clientMessageId: string;
      attachments?: PlatformAttachment[];
    }) =>
      api.post<PlatformMessage>(`/api/helpdesk/platform/tickets/${input.ticketId}/messages`, {
        body: input.body,
        client_message_id: input.clientMessageId,
        attachments: input.attachments ?? [],
      }),
    onMutate: async (input) => {
      const queryKey = ["platform-support-messages", input.ticketId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{
        items: PlatformMessage[];
        total: number;
      }>(queryKey);
      queryClient.setQueryData<{ items: PlatformMessage[]; total: number }>(
        queryKey,
        (current) => ({
          items: [
            ...(current?.items ?? []),
            {
              id: `optimistic:${input.clientMessageId}`,
              author_name: "You",
              body: input.body,
              attachments: input.attachments ?? [],
              delivery_status: "sending",
              moderation_status: "visible",
              created_at: new Date().toISOString(),
            },
          ],
          total: (current?.total ?? 0) + 1,
        }),
      );
      return { previous, queryKey };
    },
    onError: (_error, _input, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous ?? { items: [], total: 0 });
      }
    },
    onSuccess: (message, input) => {
      queryClient.setQueryData<{ items: PlatformMessage[]; total: number }>(
        ["platform-support-messages", input.ticketId],
        (current) => ({
          items: (current?.items ?? []).map((item) =>
            item.id === `optimistic:${input.clientMessageId}` ? message : item,
          ),
          total: current?.total ?? 1,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
    },
  });
}

export function useMarkPlatformTicketRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      api.post(`/api/helpdesk/platform/tickets/${ticketId}/read`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
    },
  });
}

export function useUpdatePlatformTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ticketId: string;
      status?: PlatformTicket["status"];
      assignedTo?: string | null;
    }) =>
      api.patch<PlatformTicket>(`/api/helpdesk/platform/tickets/${input.ticketId}`, {
        status: input.status,
        assigned_to: input.assignedTo,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
    },
  });
}

const PLATFORM_CHAT_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export async function uploadPlatformFile(
  ticketId: string,
  file: File,
): Promise<PlatformAttachment> {
  if (!PLATFORM_CHAT_FILE_TYPES.has(file.type) || file.size > 8 * 1024 * 1024 || file.size < 1) {
    throw new Error("Use an image, PDF, text, Word, or Excel file under 8 MB.");
  }
  const signed = await api.post<{
    put_url: string;
    object_path: string;
    required_headers: Record<string, string>;
  }>(`/api/helpdesk/platform/tickets/${ticketId}/uploads`, {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
  });
  const target = signed.put_url.startsWith("http")
    ? signed.put_url
    : new URL(signed.put_url, import.meta.env.VITE_API_URL || window.location.origin).toString();
  const uploaded = await fetch(target, {
    method: "PUT",
    headers: signed.required_headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error("The file could not be uploaded.");
  return {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
    object_path: signed.object_path,
  };
}

export async function openPlatformFile(ticketId: string, objectPath: string): Promise<void> {
  const signed = await api.get<{ get_url: string }>(
    `/api/helpdesk/platform/tickets/${ticketId}/downloads?object_path=${encodeURIComponent(objectPath)}`,
  );
  window.open(signed.get_url, "_blank", "noopener,noreferrer");
}
